import { useEffect, useRef, useState } from "react";

// Reordena a lista existente, sem remontar PDF, botões ou linhas durante o gesto.
export default function useOrdenarDocumentos({ ids, onReordenar, desabilitado = false, containerRef }) {
  const linhas = useRef(new Map());
  const gesto = useRef(null);
  const opcoes = useRef(null);
  const [arrastando, setArrastando] = useState(false);
  const [anuncio, setAnuncio] = useState("");
  opcoes.current = { ids, onReordenar, desabilitado, containerRef };

  function encerrar(aplicar) {
    const inicio = gesto.current;
    if (!inicio) return;
    gesto.current = null;
    clearTimeout(inicio.timer);
    cancelAnimationFrame(inicio.frame);
    if (inicio.linha.hasPointerCapture?.(inicio.pointerId)) inicio.linha.releasePointerCapture(inicio.pointerId);
    inicio.elementos?.forEach(({ elemento, estilo }) => {
      if (estilo === null) elemento.removeAttribute("style");
      else elemento.setAttribute("style", estilo);
    });
    inicio.flutuante?.remove();
    inicio.encaixe?.remove();
    if (!inicio.ativo) return;
    setArrastando(false);
    if (aplicar && inicio.destino !== inicio.origem) {
      const nova = [...opcoes.current.ids];
      const [item] = nova.splice(inicio.origem, 1);
      nova.splice(inicio.destino, 0, item);
      opcoes.current.onReordenar(nova);
      setAnuncio(`Documento movido da posição ${inicio.origem + 1} para ${inicio.destino + 1}. Salve para aplicar.`);
    } else setAnuncio(aplicar ? "Documento mantido na posição atual." : "Movimento cancelado.");
  }

  function pintar() {
    const atual = gesto.current;
    if (!atual?.ativo) return;
    const rect = atual.container.getBoundingClientRect();
    const ponto = atual.y - rect.top + atual.container.scrollTop;
    const restantes = atual.elementos.filter((item) => item.id !== atual.id);
    atual.destino = restantes.filter((item) => ponto > item.top + item.altura / 2).length;
    const nova = [...restantes];
    nova.splice(atual.destino, 0, atual.elementos[atual.origem]);
    let top = atual.elementos[0].top;
    let topEncaixe = top;
    nova.forEach((item) => {
      if (item.id === atual.id) topEncaixe = top;
      else item.elemento.style.transform = `translateY(${top - item.top}px)`;
      top += item.altura + atual.intervalo;
    });
    Object.assign(atual.flutuante.style, {
      top: `${Math.max(rect.top + 2, Math.min(rect.bottom - atual.altura - 2, atual.y - atual.offsetY))}px`,
      left: `${atual.left}px`,
    });
    const topTela = rect.top + topEncaixe - atual.container.scrollTop;
    Object.assign(atual.encaixe.style, { top: `${topTela}px`, visibility: topTela < rect.top || topTela + atual.altura > rect.bottom ? "hidden" : "visible" });
    atual.encaixe.textContent = `Solte na posição ${atual.destino + 1}`;
  }

  function rolar(agora) {
    const atual = gesto.current;
    if (!atual?.ativo) return;
    const rect = atual.container.getBoundingClientRect();
    const faixa = Math.min(58, rect.height / 3);
    const delta = atual.tempo ? Math.min(32, agora - atual.tempo) : 16;
    atual.tempo = agora;
    let velocidade = atual.y < rect.top + faixa ? -Math.min(13, (rect.top + faixa - atual.y) / faixa * 13)
      : atual.y > rect.bottom - faixa ? Math.min(13, (atual.y - rect.bottom + faixa) / faixa * 13) : 0;
    if (atual.x < rect.left - 20 || atual.x > rect.right + 20) velocidade = 0;
    atual.container.scrollTop += velocidade * delta / 16;
    pintar();
    atual.frame = requestAnimationFrame(rolar);
  }

  function ativar() {
    const atual = gesto.current;
    if (!atual || atual.ativo) return;
    clearTimeout(atual.timer);
    const rect = atual.linha.getBoundingClientRect();
    const containerRect = atual.container.getBoundingClientRect();
    const documento = atual.linha.ownerDocument;
    const tela = documento.defaultView;
    atual.altura = rect.height;
    atual.left = rect.left;
    atual.ativo = true;
    atual.elementos = opcoes.current.ids.map((id) => {
      const elemento = linhas.current.get(String(id));
      if (!elemento) return null;
      const medida = elemento.getBoundingClientRect();
      return { id: String(id), elemento, estilo: elemento.getAttribute("style"), top: medida.top - containerRect.top + atual.container.scrollTop, altura: medida.height };
    });
    if (atual.elementos.some((item) => !item)) { encerrar(false); return; }
    atual.intervalo = atual.elementos.length > 1 ? Math.max(0, atual.elementos[1].top - atual.elementos[0].top - atual.elementos[0].altura) : 0;
    const flutuante = documento.createElement("div");
    flutuante.setAttribute("aria-hidden", "true");
    flutuante.setAttribute("inert", "");
    const cores = tela.getComputedStyle(atual.linha);
    Object.assign(flutuante.style, { position: "fixed", zIndex: "1600", pointerEvents: "none", width: `${rect.width}px`, height: `${rect.height}px`, border: "1px solid var(--sear-azul, #087cf0)", borderRadius: "7px", background: "var(--sear-superficie, #fff)", boxShadow: "0 10px 24px rgba(0,0,0,.22)", color: cores.color, font: cores.font, overflow: "hidden", transform: "rotate(-1deg)" });
    const clone = atual.linha.cloneNode(true);
    Object.assign(clone.style, { display: cores.display, gridTemplateColumns: cores.gridTemplateColumns, gap: cores.gap, padding: cores.padding, width: '100%', height: '100%' });
    clone.removeAttribute("id");
    clone.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
    // Os seletores de tabela não alcançam o portal; copia a geometria das células.
    if (atual.linha.tagName === "TR") {
      const tabela = documento.createElement("table");
      tabela.style.cssText = "border-collapse:collapse;width:100%;height:100%;table-layout:fixed";
      const corpo = documento.createElement("tbody");
      Array.from(atual.linha.children).forEach((celula, index) => {
        const estilo = tela.getComputedStyle(celula);
        const destino = clone.children[index];
        Object.assign(destino.style, { display: estilo.display, gridColumn: estilo.gridColumn, width: cores.display === 'grid' ? 'auto' : `${celula.getBoundingClientRect().width}px`, padding: estilo.padding, font: estilo.font, color: estilo.color, textAlign: estilo.textAlign, verticalAlign: estilo.verticalAlign, border: "none" });
      });
      corpo.append(clone); tabela.append(corpo); flutuante.append(tabela);
    } else flutuante.append(clone);
    const encaixe = documento.createElement("div");
    encaixe.setAttribute("aria-hidden", "true");
    Object.assign(encaixe.style, { position: "fixed", zIndex: "1500", pointerEvents: "none", left: `${rect.left}px`, width: `${rect.width}px`, height: `${rect.height}px`, boxSizing: "border-box", border: "2px dashed var(--sear-azul, #087cf0)", borderRadius: "6px", background: "var(--sear-superficie-azul, #eaf4ff)", color: "var(--sear-texto, #173f72)", fontSize: "12px", display: "grid", placeItems: "center" });
    documento.body.append(encaixe, flutuante);
    atual.flutuante = flutuante; atual.encaixe = encaixe;
    atual.elementos.forEach(({ elemento }) => { elemento.style.transition = tela.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "none" : "transform 140ms ease"; });
    atual.linha.style.visibility = "hidden";
    pintar(); setArrastando(true); setAnuncio("Movendo documento. Solte para encaixar; Escape cancela.");
    atual.frame = requestAnimationFrame(rolar);
  }

  function iniciar(event, id) {
    const config = opcoes.current;
    if (config.desabilitado || gesto.current || event.button !== 0 || config.ids.length < 2) return;
    const alca = event.target.closest("[data-alca-ordem]");
    if ((!alca && event.pointerType === "touch") || (!alca && event.target.closest("button,a,input,select,textarea,[role=button],[contenteditable]"))) return;
    const linha = event.currentTarget;
    const container = config.containerRef.current;
    if (!container) return;
    const rect = linha.getBoundingClientRect();
    const origem = config.ids.findIndex((item) => String(item) === String(id));
    gesto.current = { id: String(id), linha, container, origem, destino: origem, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY, offsetY: event.clientY - rect.top, ativo: false };
    linha.setPointerCapture?.(event.pointerId);
    gesto.current.timer = setTimeout(ativar, event.pointerType === "touch" ? 250 : 160);
  }

  useEffect(() => {
    const mover = (event) => {
      const atual = gesto.current;
      if (!atual || atual.pointerId !== event.pointerId) return;
      atual.x = event.clientX; atual.y = event.clientY;
      if (!atual.ativo && Math.hypot(atual.x - atual.startX, atual.y - atual.startY) > 5) ativar();
      if (gesto.current?.ativo) { event.preventDefault(); pintar(); }
    };
    const soltar = (event) => {
      const atual = gesto.current;
      if (!atual || atual.pointerId !== event.pointerId) return;
      const rect = atual.container.getBoundingClientRect();
      atual.x = event.clientX; atual.y = event.clientY; pintar();
      encerrar(event.clientX >= rect.left - 12 && event.clientX <= rect.right + 12 && event.clientY >= rect.top - 25 && event.clientY <= rect.bottom + 25);
    };
    const cancelar = () => encerrar(false);
    const teclado = (event) => { if (event.key === "Escape" && gesto.current) { event.preventDefault(); cancelar(); } };
    window.addEventListener("pointermove", mover, { passive: false });
    window.addEventListener("pointerup", soltar);
    window.addEventListener("pointercancel", cancelar);
    window.addEventListener("blur", cancelar);
    window.addEventListener("keydown", teclado);
    return () => { window.removeEventListener("pointermove", mover); window.removeEventListener("pointerup", soltar); window.removeEventListener("pointercancel", cancelar); window.removeEventListener("blur", cancelar); window.removeEventListener("keydown", teclado); cancelar(); };
    // Eventos usam refs para não interromper o gesto a cada renderização da linha.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const assinatura = ids.map(String).join(",");
  useEffect(() => {
    encerrar(false);
    // Cancela se a lista/permissão mudar; o gesto e as opções estão em refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinatura, desabilitado]);

  const propsLinha = (id) => ({
    ref: (el) => { if (el) linhas.current.set(String(id), el); else linhas.current.delete(String(id)); },
    onPointerDown: (event) => iniciar(event, id),
    onLostPointerCapture: () => { if (gesto.current?.id === String(id)) encerrar(false); },
    onDragStart: (event) => event.preventDefault(),
  });
  const propsAlca = (id) => ({
    "data-alca-ordem": "",
    "aria-label": `Mover documento da posição ${ids.findIndex((item) => String(item) === String(id)) + 1}. Segure e arraste ou use as setas.`,
    disabled: desabilitado || arrastando || ids.length < 2,
    style: { cursor: desabilitado ? "default" : "grab", touchAction: "none" },
    onKeyDown: (event) => {
      if (desabilitado || gesto.current || !["ArrowUp", "ArrowDown"].includes(event.key)) return;
      event.preventDefault();
      const origem = ids.findIndex((item) => String(item) === String(id));
      const destino = origem + (event.key === "ArrowUp" ? -1 : 1);
      if (destino < 0 || destino >= ids.length) return;
      const nova = [...ids]; const [item] = nova.splice(origem, 1); nova.splice(destino, 0, item);
      onReordenar(nova); setAnuncio(`Documento movido para a posição ${destino + 1}. Salve para aplicar.`);
    },
  });
  return { propsLinha, propsAlca, arrastando, anuncio };
}
